import { ReactElement, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import { siteContext } from '@gn-audio/jss-core';
import { getLocaleWithRegion } from '@gn-audio/jss-core/common/utilities';

import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { Button } from '../../Button/Button';
import { EcomCountryCode } from '../../LanguageSelector/LanguageSelectorButton';
import { useHeaderNavigation } from '../../MegaMenu';
import { useClientLocation } from '../../MegaMenu/providers/ClientLocationContext';
import CountriesSelectComponent from './CountriesSelect/CountriesSelectComponent';
import { getEcomDomains } from './CountryConstants';

import basketStyles from '../Basket.module.css';
import locationStyles from './Location.module.css';

const Location = (): ReactElement | null => {
  const { asPath } = useRouter();
  const { t } = useFallbackTranslation();
  const { currentLocale } = useHeaderNavigation();
  const localeWithRegion = getLocaleWithRegion(currentLocale);

  const ecomCountries = getEcomDomains(siteContext);

  const location = useClientLocation();
  const userCountryCode = location.countryCode;

  const contextCountry = useMemo(
    () => ecomCountries.find(({ code }) => code === userCountryCode),
    [ecomCountries, userCountryCode]
  );

  useEffect(() => {
    setSelectedCountry(contextCountry);
  }, [contextCountry]);

  const [selectedCountry, setSelectedCountry] = useState(contextCountry);

  const shouldShowShippingMessage = useMemo(() => {
    if (!userCountryCode) {
      return false;
    }

    const ecomEnabledForUserCountry: boolean = ecomCountries.some(
      ({ code }) => code === userCountryCode
    );

    return (
      localeWithRegion.toLowerCase() === 'en-us' &&
      ecomEnabledForUserCountry &&
      userCountryCode !== EcomCountryCode.UnitedStates
    );
  }, [localeWithRegion, userCountryCode, ecomCountries]);

  if (!shouldShowShippingMessage) return null;

  const handleGoToLocation = () => {
    if (!selectedCountry) return;
    window.location.href = selectedCountry.url + asPath.replace(/^\//, '');
  };

  const [language] = localeWithRegion.split('-');
  const regionNames = new Intl.DisplayNames([language], { type: 'region' });
  const country = regionNames.of(userCountryCode ?? 'us');

  return (
    <div className={classNames(basketStyles.card, basketStyles.grey, locationStyles.container)}>
      <p className={locationStyles.locationInfo}>
        <span className={classNames(basketStyles.topLine, basketStyles.fat)}>
          {t('cart.ShippingOutsideOfNorthAmerica')}
        </span>
        <span className={locationStyles.bottom}>
          {t('cart.CannotShipToCurrentLocation')} (
          <span className={basketStyles.fat}>{country}</span>
          ). <br />
          {t('cart.SelectDifferentWebsite')}
        </span>
      </p>
      <div className="flex-0 space-between flex flex-row flex-wrap gap-3">
        <CountriesSelectComponent
          countries={ecomCountries}
          selected={selectedCountry}
          onChange={setSelectedCountry}
        />
        <Button variant="primary" size="l" onClick={handleGoToLocation}>
          {t('cart.Go')}
        </Button>
      </div>
    </div>
  );
};

export default Location;
